import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Input,
  SectionHeader,
  Skeleton,
  Textarea,
  useActionDialog,
} from '../components/ui';
import {
  useBankAccounts,
  useCreateBankAccount,
  useDeactivateBankAccount,
  useUpdateBankAccount,
} from '../features/patients/hooks/useBankAccounts';
import type { BankAccount } from '../types/admin';
import styles from './BankAccountsPage.module.scss';

export function BankAccountsPage() {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const { data: accounts, isLoading, isError, error } = useBankAccounts();
  const createMutation = useCreateBankAccount();
  const updateMutation = useUpdateBankAccount();
  const deactivateMutation = useDeactivateBankAccount();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [cName, setCName] = useState('');
  const [cBank, setCBank] = useState('');
  const [cIban, setCIban] = useState('');
  const [cSwift, setCSwift] = useState('');
  const [cAccountNumber, setCAccountNumber] = useState('');
  const [cCurrency, setCCurrency] = useState('UAH');
  const [cPrivacyOffer, setCPrivacyOffer] = useState('');

  const [eName, setEName] = useState('');
  const [eBank, setEBank] = useState('');
  const [eIban, setEIban] = useState('');
  const [eSwift, setESwift] = useState('');
  const [eAccountNumber, setEAccountNumber] = useState('');
  const [eCurrency, setECurrency] = useState('UAH');
  const [ePrivacyOffer, setEPrivacyOffer] = useState('');

  function resetCreateForm() {
    setCName('');
    setCBank('');
    setCIban('');
    setCSwift('');
    setCAccountNumber('');
    setCCurrency('UAH');
    setCPrivacyOffer('');
  }

  function openCreate() {
    setEditingId(null);
    setShowCreate(true);
    resetCreateForm();
  }

  function openEdit(acc: BankAccount) {
    setShowCreate(false);
    setEditingId(acc.id);
    setEName(acc.account_name);
    setEBank(acc.bank_name);
    setEIban(acc.iban);
    setESwift(acc.swift_code ?? '');
    setEAccountNumber(acc.account_number ?? '');
    setECurrency(acc.currency);
    setEPrivacyOffer(acc.privacy_offer ?? '');
  }

  function closeForms() {
    setShowCreate(false);
    setEditingId(null);
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await createMutation.mutateAsync({
        account_name: cName.trim(),
        iban: cIban.trim(),
        bank_name: cBank.trim(),
        swift_code: cSwift.trim() || undefined,
        account_number: cAccountNumber.trim() || undefined,
        currency: cCurrency.trim() || 'UAH',
        privacy_offer: cPrivacyOffer.trim() || undefined,
      });
      setShowCreate(false);
      resetCreateForm();
    } catch {
      return;
    }
  }

  async function handleUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingId) return;
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        payload: {
          account_name: eName.trim(),
          iban: eIban.trim(),
          bank_name: eBank.trim(),
          swift_code: eSwift.trim() ? eSwift.trim() : null,
          account_number: eAccountNumber.trim() ? eAccountNumber.trim() : null,
          currency: eCurrency.trim(),
          privacy_offer: ePrivacyOffer.trim() ? ePrivacyOffer.trim() : null,
        },
      });
      setEditingId(null);
    } catch {
      return;
    }
  }

  async function handleDeactivate(id: string) {
    if (!(await confirmAction({
      message: t('bankAccounts.confirmDeactivate'),
      confirmLabel: t('bankAccounts.deactivate'),
      tone: 'warning',
    }))) return;
    deactivateMutation.mutate(id);
  }

  const busy =
    createMutation.isPending || updateMutation.isPending || deactivateMutation.isPending;

  if (isLoading) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('bankAccounts.title')} subtitle={t('bankAccounts.subtitle')} />
          <div className={styles.skeletonList}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} height={72} />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('bankAccounts.title')} />
          <EmptyState
            title={t('bankAccounts.loadErrorTitle')}
            description={error instanceof Error ? error.message : t('bankAccounts.loadErrorDescription')}
          />
        </div>
      </AppShell>
    );
  }

  const list = accounts ?? [];

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader
          title={t('bankAccounts.title')}
          subtitle={t('bankAccounts.subtitle')}
          actions={
            <Button variant="primary" onClick={openCreate} disabled={busy}>
              {t('bankAccounts.addAccount')}
            </Button>
          }
        />

        {showCreate ? (
          <Card padding="none">
            <CardHeader title={t('bankAccounts.newAccountTitle')} />
            <form onSubmit={handleCreate}>
              <CardBody>
                <div className={styles.formGrid}>
                  <Input
                    label={t('bankAccounts.labelAccountName')}
                    value={cName}
                    onChange={(e) => setCName(e.target.value)}
                    placeholder={t('bankAccounts.placeholderAccountName')}
                  />
                  <Input
                    label={t('bankAccounts.labelBankName')}
                    value={cBank}
                    onChange={(e) => setCBank(e.target.value)}
                    placeholder={t('bankAccounts.placeholderBank')}
                  />
                  <Input
                    wrapperClassName={styles.fieldFull}
                    label={t('bankAccounts.labelIban')}
                    value={cIban}
                    onChange={(e) => setCIban(e.target.value)}
                    placeholder={t('bankAccounts.placeholderIban')}
                  />
                  <Input
                    label={t('bankAccounts.labelSwift')}
                    value={cSwift}
                    onChange={(e) => setCSwift(e.target.value)}
                  />
                  <Input
                    label={t('bankAccounts.labelCurrency')}
                    value={cCurrency}
                    onChange={(e) => setCCurrency(e.target.value)}
                    maxLength={3}
                  />
                  <Input
                    wrapperClassName={styles.fieldFull}
                    label={t('bankAccounts.labelAccountNumber')}
                    value={cAccountNumber}
                    onChange={(e) => setCAccountNumber(e.target.value)}
                  />
                  <Textarea
                    wrapperClassName={styles.fieldFull}
                    className={styles.privacyOfferTextarea}
                    label={t('bankAccounts.labelPrivacyOffer')}
                    value={cPrivacyOffer}
                    onChange={(e) => setCPrivacyOffer(e.target.value)}
                    placeholder={t('bankAccounts.placeholderPrivacyOffer')}
                    rows={6}
                  />
                </div>
                {createMutation.error instanceof Error ? (
                  <p className={styles.inlineError}>{createMutation.error.message}</p>
                ) : null}
              </CardBody>
              <CardFooter>
                <Button variant="secondary" type="button" onClick={closeForms} disabled={busy}>
                  {t('bankAccounts.cancel')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={
                    busy || !cName.trim() || !cBank.trim() || !cIban.trim() || !cCurrency.trim()
                  }
                >
                  {createMutation.isPending ? t('bankAccounts.creating') : t('bankAccounts.create')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : null}

        {editingId ? (
          <Card padding="none">
            <CardHeader title={t('bankAccounts.editAccountTitle')} />
            <form onSubmit={handleUpdate}>
              <CardBody>
                <div className={styles.formGrid}>
                  <Input
                    label={t('bankAccounts.labelAccountName')}
                    value={eName}
                    onChange={(e) => setEName(e.target.value)}
                  />
                  <Input
                    label={t('bankAccounts.labelBankName')}
                    value={eBank}
                    onChange={(e) => setEBank(e.target.value)}
                  />
                  <Input
                    wrapperClassName={styles.fieldFull}
                    label={t('bankAccounts.labelIban')}
                    value={eIban}
                    onChange={(e) => setEIban(e.target.value)}
                  />
                  <Input
                    label={t('bankAccounts.labelSwift')}
                    value={eSwift}
                    onChange={(e) => setESwift(e.target.value)}
                  />
                  <Input
                    label={t('bankAccounts.labelCurrency')}
                    value={eCurrency}
                    onChange={(e) => setECurrency(e.target.value)}
                    maxLength={3}
                  />
                  <Input
                    wrapperClassName={styles.fieldFull}
                    label={t('bankAccounts.labelAccountNumber')}
                    value={eAccountNumber}
                    onChange={(e) => setEAccountNumber(e.target.value)}
                  />
                  <Textarea
                    wrapperClassName={styles.fieldFull}
                    className={styles.privacyOfferTextarea}
                    label={t('bankAccounts.labelPrivacyOffer')}
                    value={ePrivacyOffer}
                    onChange={(e) => setEPrivacyOffer(e.target.value)}
                    placeholder={t('bankAccounts.placeholderPrivacyOffer')}
                    rows={6}
                  />
                </div>
                {updateMutation.error instanceof Error ? (
                  <p className={styles.inlineError}>{updateMutation.error.message}</p>
                ) : null}
              </CardBody>
              <CardFooter>
                <Button variant="secondary" type="button" onClick={closeForms} disabled={busy}>
                  {t('bankAccounts.cancel')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={busy || !eName.trim() || !eBank.trim() || !eIban.trim() || !eCurrency.trim()}
                >
                  {updateMutation.isPending ? t('bankAccounts.saving') : t('bankAccounts.save')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : null}

        {list.length === 0 && !showCreate ? (
          <EmptyState title={t('bankAccounts.emptyTitle')} description={t('bankAccounts.emptyDescription')} />
        ) : list.length > 0 ? (
          <div className={styles.list}>
            {list.map((acc) => (
              <Card key={acc.id} padding="md">
                <div className={styles.row}>
                  <div className={styles.identity}>
                    <div className={styles.nameLine}>
                      <span className={styles.name}>{acc.account_name}</span>
                      <Badge variant="neutral" size="sm">
                        {acc.currency}
                      </Badge>
                    </div>
                    <div className={styles.meta}>
                      <span className={styles.metaItem}>
                        <span className={styles.metaLabel}>{t('bankAccounts.colBank')}</span>
                        {acc.bank_name}
                      </span>
                      <span className={styles.metaItem}>
                        <span className={styles.metaLabel}>{t('bankAccounts.colIban')}</span>
                        {acc.iban}
                      </span>
                      {acc.swift_code ? (
                        <span className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('bankAccounts.colSwift')}</span>
                          {acc.swift_code}
                        </span>
                      ) : null}
                      {acc.account_number ? (
                        <span className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('bankAccounts.colAccountNumber')}</span>
                          {acc.account_number}
                        </span>
                      ) : null}
                    </div>
                    {acc.privacy_offer ? (
                      <div className={styles.privacyOfferBlock}>
                        <span className={styles.metaLabel}>{t('bankAccounts.colPrivacyOffer')}</span>
                        <p className={styles.privacyOfferPreview}>{acc.privacy_offer}</p>
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.rowActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(acc)}
                      disabled={busy || showCreate || (editingId !== null && editingId !== acc.id)}
                    >
                      {t('bankAccounts.edit')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeactivate(acc.id)}
                      disabled={busy}
                    >
                      {t('bankAccounts.deactivate')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
